import { NgClass } from '@angular/common';
import { Component, OnDestroy, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelContent,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { StyleService } from '@jfw/angular/ecommerce/core';
import { setColorClass } from '@jfw/business-logic';
import { FilterSelect } from '@jfw/types';
import { Subscription } from 'rxjs';

type FilterOption = {
  value: string;
  selected?: boolean;
  control?: FormControl<boolean | null>;
};

export type FilterCategory = {
  attribute: string;
  displayName: string;
  select?: FilterSelect;
  options?: Array<FilterOption>;
  ranking?: number;
};

// Interface for the search form.
interface SearchForm {
  search: FormControl<string | null>;
}

@Component({
  selector: 'app-style-filters',
  templateUrl: './style-listing-page-filters.component.html',
  styleUrls: ['./style-listing-page-filters.component.scss'],
  imports: [
    MatAccordion,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatButton,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelContent,
    MatCheckbox,
    NgClass,
  ],
})
export class StyleListingPageFiltersComponent implements OnDestroy {
  // We use toSignal to get the filter categories.
  filterCategories = toSignal(
    this.styleService.filterCategories,
  ) as () => FilterCategory[];
  colorMap: Record<string, string> = {};

  // The search form is now a separate form group.
  form = new FormGroup<SearchForm>({
    search: new FormControl(''),
  });

  // We use a single Subscription object to manage all checkbox subscriptions.
  private subscriptions = new Subscription();

  constructor(private styleService: StyleService) {
    // We use an effect to react to changes in the filterCategories signal.
    effect(() => {
      const categories = this.filterCategories();
      if (categories) {
        // Clear all previous subscriptions to avoid memory leaks.
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();

        // Loop through each category and its options to create a form control for each.
        categories.forEach((category) => {
          category.options?.forEach((option) => {
            // Create a new FormControl for this specific option.
            const newControl = new FormControl(option.selected ?? false);
            // Assign the control directly to the option object.
            option.control = newControl;

            // Subscribe to the value changes of this new control.
            this.subscriptions.add(
              newControl.valueChanges.subscribe(() => {
                // When a checkbox value changes, update the service.
                this.syncCheckboxesWithService();
              }),
            );
          });
        });
      }
    });
  }

  // Method to synchronize the form state with the service state.
  private syncCheckboxesWithService(): void {
    console.log('DEBUG: Checkbox sync triggered search');
    const categories = this.filterCategories();
    if (!categories) return;

    categories.forEach((category) => {
      category.options?.forEach((option) => {
        option.selected = !!(option.control && option.control.value);
      });
    });

    // CRITICAL: We do NOT update this.styleService.searchText here.
    // This allows the live checkbox update to respect the LAST searched style code.
    this.styleService.searchStyles.next();
  }

  clearSearch(): void {
    this.form.controls.search.setValue('');
    this.styleService.searchText.next('');
    // Trigger search to show results without the style code filter
    this.styleService.searchStyles.next();
  }

  // Method to handle the main search button click.
  onSearch(): void {
    const searchText = this.form.controls.search.value ?? '';
    this.styleService.searchText.next(searchText);
    //this.styleService.searchStyles.next();
  }

  // Method to clear all filters.
  clearFilters(): void {
    // Clear the search input control.
    this.form.controls.search.setValue('');
    this.styleService.searchText.next('');

    // Loop through all categories and their options and set their controls to false.
    // const categories = this.filterCategories();
    // if (categories) {
    //   categories.forEach((category) => {
    //     category.options?.forEach((option) => {
    //       option.control?.setValue(false, { emitEvent: false });
    //       option.selected = false;
    //     });
    //   });
    // }
    this.styleService.searchStyles.next();
  }

  setColorClass(colorFamily: string, checkbox?: MatCheckbox): string {
    const nativeElement = checkbox?._elementRef.nativeElement;
    return setColorClass(colorFamily, nativeElement);
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions to prevent memory leaks.
    this.subscriptions.unsubscribe();
  }
}
